import { SEO, breadcrumbSchema } from "../components/SEO";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { business } from "../content/site";
import { legalEn } from "../content/en";
import { useLang, useLp } from "../i18n/lang";
import { useCopy } from "../i18n/copy";

const pages = {
  "aviso-legal": {
    title: "Aviso legal",
    seoTitle: "Aviso legal | MideFinca",
    metaDescription: "Aviso legal de MideFinca: titular del sitio, condiciones de uso, propiedad intelectual y legislación aplicable.",
    updated: "23 de agosto de 2026",
    sections: [
      {
        h2: "Titular del sitio web",
        paragraphs: [
          "En cumplimiento del artículo 10 de la Ley 34/2002, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se identifica al titular de este sitio web:",
          `Titular: ${business.legalName}`,
          `NIF: ${business.nif}`,
          `Domicilio: ${business.legalAddress}`,
          `Email: ${business.email} · Teléfono: ${business.phoneDisplay}`,
          "Dominio: https://midefinca.es",
        ],
      },
      {
        h2: "Información sobre el ejercicio de profesión regulada",
        paragraphs: [
          "La dirección técnica de los servicios ofrecidos corresponde al ejercicio de una profesión regulada, con los siguientes datos:",
        ],
        list: [
          "Profesión: Arquitecto",
          "Titulación oficial: Arquitecto",
          "Estado en el que se expidió el título: España",
          "Colegio profesional: Colegio Oficial de Arquitectos de Tenerife, La Gomera y El Hierro",
          "Número de colegiado: 3665",
        ],
      },
      {
        h2: "Normas profesionales aplicables",
        paragraphs: [
          "El ejercicio de la profesión se rige por las siguientes normas profesionales y deontológicas:",
        ],
        list: [
          "Estatutos Generales de los Colegios Oficiales de Arquitectos y su Consejo Superior.",
          "Estatuto del Consejo Canario de Colegios de Arquitectos.",
          "Estatuto Particular del Colegio Oficial de Arquitectos de Tenerife, La Gomera y El Hierro.",
          "Código Deontológico de los Arquitectos.",
        ],
        after: [
          {
            text: "Estas normas pueden consultarse electrónicamente en la sección de normativa del Colegio Oficial de Arquitectos de Tenerife, La Gomera y El Hierro (COACTFE): ",
            href: "https://www.coactfe.org/institucional",
            linkText: "https://www.coactfe.org/institucional",
          },
        ],
      },
      {
        h2: "Objeto del sitio web",
        paragraphs: [
          "midefinca.es proporciona información y servicios técnicos relacionados con la medición de fincas y terrenos, levantamientos topográficos, georreferenciación, archivos GML y representación gráfica, trabajos técnicos vinculados a Catastro y al Registro de la Propiedad, comprobación de superficies, lindes y deslindes, certificados técnicos y, en general, documentación técnica de terrenos e inmuebles.",
        ],
      },
      {
        h2: "Condiciones de utilización",
        paragraphs: [
          "El acceso y uso de este sitio web atribuye la condición de usuario e implica la aceptación de las presentes condiciones. El usuario se compromete a hacer un uso lícito, diligente, honesto y correcto del sitio, de acuerdo con la legislación aplicable, la buena fe y el orden público, y a no utilizarlo para dañar su disponibilidad, su seguridad o los derechos del titular o de terceros.",
        ],
      },
      {
        h2: "Información técnica y resultado de procedimientos ante terceros",
        paragraphs: [
          "La información publicada en este sitio web, incluidas las guías y los artículos, tiene carácter general e informativo y no sustituye el estudio individualizado de cada inmueble, finca, expediente o documentación.",
          "Las cuestiones relacionadas con Catastro, el Registro de la Propiedad, notarías, ayuntamientos, validaciones gráficas, procedimientos de subsanación, expedientes administrativos, aceptación de representaciones gráficas o la coordinación entre Catastro y Registro pueden depender de la documentación existente, de terceros, incluidos los titulares colindantes, y de los criterios o resoluciones de los organismos competentes. En consecuencia, la contratación de un trabajo técnico no debe interpretarse como garantía de que una administración, Catastro, el Registro, un notario u otro organismo vaya a resolver un procedimiento en un sentido determinado.",
          "Lo anterior se entiende sin perjuicio de la responsabilidad que corresponda al prestador por el propio trabajo técnico realizado, conforme a la legislación vigente.",
        ],
      },
      {
        h2: "Datos facilitados por el cliente o por terceros",
        paragraphs: [
          "Los trabajos técnicos podrán realizarse tomando como base documentación o información facilitada por el cliente o por terceros. El titular no será responsable de errores derivados exclusivamente de datos falsos, incompletos, incorrectos o desactualizados aportados por el cliente o por terceros, siempre dentro de los límites permitidos por la legislación aplicable.",
        ],
      },
      {
        h2: "Contenido del sitio y actualidad normativa",
        paragraphs: [
          "Se procura mantener la información actualizada; no obstante, la normativa, los procedimientos administrativos, los criterios técnicos y los trámites pueden cambiar con posterioridad a la publicación de cada contenido. El titular no garantiza que la información refleje en todo momento la situación normativa o administrativa vigente. Las guías y artículos no constituyen por sí solos asesoramiento técnico individualizado y no deben fundamentar decisiones sin el estudio del caso concreto.",
        ],
      },
      {
        h2: "Propiedad intelectual e industrial",
        paragraphs: [
          "El signo distintivo MideFinca, el logotipo, los textos, gráficos, planos, fotografías, infografías, diseños, el contenido técnico y la estructura y diseño de este sitio web son titularidad del titular o se utilizan con autorización o licencia. Queda prohibida su reproducción, distribución, comunicación pública, transformación o explotación no autorizada, en los términos previstos por la legislación de propiedad intelectual e industrial.",
        ],
      },
      {
        h2: "Enlaces a sitios de terceros",
        paragraphs: [
          "Este sitio puede incluir enlaces a sitios de terceros (por ejemplo, Catastro, Registro de la Propiedad, administraciones públicas o servicios de mensajería) por comodidad del usuario. El titular no controla ni responde de los contenidos, la disponibilidad ni las políticas de dichos sitios, sin perjuicio de la responsabilidad que legalmente pueda corresponderle.",
        ],
      },
      {
        h2: "Disponibilidad y seguridad del sitio",
        paragraphs: [
          "El titular adopta las medidas técnicas razonables para el correcto funcionamiento del sitio. No obstante, no puede garantizar una disponibilidad ininterrumpida ni la ausencia de errores, y no responderá de interrupciones o daños derivados de trabajos de mantenimiento, fallos de proveedores de terceros, ataques informáticos u otras causas fuera de su control razonable, dentro de los límites permitidos por la legislación vigente.",
        ],
      },
      {
        h2: "Legislación aplicable",
        paragraphs: [
          "Este sitio web y sus condiciones se rigen por la legislación española. Para la resolución de controversias serán competentes los juzgados y tribunales que resulten conforme a la legislación aplicable; en particular, cuando el usuario tenga la condición de consumidor, serán competentes los juzgados que imperativamente determine la normativa de consumidores y usuarios.",
        ],
      },
    ],
  },
  "politica-de-privacidad": {
    title: "Política de privacidad",
    seoTitle: "Política de privacidad | MideFinca",
    metaDescription: "Política de privacidad de MideFinca: responsable, datos tratados, finalidades, bases jurídicas, conservación, destinatarios y derechos (RGPD y LOPDGDD).",
    updated: "15 de julio de 2026",
    sections: [
      {
        h2: "Responsable del tratamiento",
        paragraphs: [
          `Responsable: ${business.legalName} · NIF: ${business.nif} · Domicilio: ${business.legalAddress}`,
          `Email: ${business.email} · Teléfono: ${business.phoneDisplay} · Sitio web: https://midefinca.es`,
        ],
      },
      {
        h2: "Datos personales que se tratan",
        paragraphs: [
          "Esta web trata únicamente los datos necesarios para atender las solicitudes recibidas. Concretamente:",
        ],
        list: [
          "Formulario de contacto y solicitud de presupuesto: nombre, teléfono, correo electrónico, municipio, tipo de necesidad, descripción del caso, referencia catastral (opcional) y documentación aportada voluntariamente (PDF, JPG o PNG).",
          "Contacto directo por teléfono, correo electrónico o WhatsApp: los datos que el usuario facilite en esa comunicación. Si utilizas WhatsApp, la comunicación se realiza a través del servicio de WhatsApp (Meta), al que se aplican sus propias condiciones y política de privacidad.",
          "Gestión de encargos: los datos necesarios para la prestación del servicio contratado, la documentación del inmueble y la facturación.",
          "Navegación: los registros técnicos del servidor (dirección IP, fecha y páginas solicitadas), con fines de seguridad y funcionamiento.",
          "Esta web no utiliza los datos para publicidad ni boletines, y no dispone de sistemas de analítica o seguimiento activos en este momento.",
        ],
      },
      {
        h2: "Finalidades y bases jurídicas",
        paragraphs: ["Cada tratamiento se apoya en la base jurídica que le corresponde conforme al artículo 6 del RGPD:"],
        list: [
          "Contestar consultas, estudiar el caso y elaborar presupuestos: aplicación de medidas precontractuales a petición del interesado (art. 6.1.b RGPD).",
          "Prestar los servicios contratados y gestionar la relación profesional: ejecución de un contrato (art. 6.1.b RGPD).",
          "Facturación y cumplimiento de obligaciones fiscales, contables y legales: cumplimiento de obligaciones legales (art. 6.1.c RGPD).",
          "Registros técnicos de seguridad del servidor: interés legítimo en garantizar la seguridad del sitio (art. 6.1.f RGPD).",
          "Analítica web, si se activara en el futuro: consentimiento (art. 6.1.a RGPD), solicitado previamente y de forma independiente.",
        ],
      },
      {
        h2: "Plazos de conservación",
        paragraphs: [
          "Los datos de consultas que no lleguen a formalizar un encargo se conservarán durante el tiempo necesario para atender la solicitud y, con carácter general, un máximo de doce meses para poder atender aclaraciones posteriores relacionadas con ella, salvo que se solicite antes su supresión.",
          "Los datos de clientes y la documentación de los encargos se conservarán durante la relación profesional y, una vez finalizada, durante los plazos exigidos por la legislación fiscal, contable y de prescripción de responsabilidades (con carácter general, cuatro años en materia tributaria y seis años en materia mercantil y contable).",
          "Los registros técnicos del servidor se conservan durante plazos breves de seguridad, conforme a la configuración habitual del proveedor de alojamiento.",
        ],
      },
      {
        h2: "Destinatarios de los datos",
        paragraphs: [
          "No se ceden datos personales a terceros con fines comerciales ni publicitarios. Los datos podrán comunicarse únicamente cuando sea necesario para el servicio contratado o exista obligación legal:",
        ],
        list: [
          "Administraciones públicas, Catastro, Registro de la Propiedad, notarías u otros profesionales intervinientes, cuando el expediente o servicio contratado lo requiera.",
          "Administración tributaria y demás organismos, cuando exista obligación legal.",
          "Asesoría o gestoría para el cumplimiento de obligaciones fiscales y contables.",
          "Entidades financieras para la gestión de cobros.",
        ],
      },
      {
        h2: "Encargados del tratamiento",
        paragraphs: [
          "Para el funcionamiento técnico del sitio intervienen los siguientes proveedores con acceso limitado a los datos, en calidad de encargados del tratamiento:",
        ],
        list: [
          "Proveedor de alojamiento y plataforma técnica del sitio web y su base de datos.",
          "Servicio de correo transaccional utilizado para remitir al titular las solicitudes del formulario (Resend, a través de la plataforma Emergent).",
          "Proveedor de correo electrónico del titular (OVH), cuando la comunicación se realiza por email.",
        ],
      },
      {
        h2: "Transferencias internacionales de datos",
        paragraphs: [
          "Algunos de los proveedores tecnológicos indicados pueden tratar datos fuera del Espacio Económico Europeo. En ese caso, el tratamiento se amparará en los mecanismos de garantía previstos por el RGPD, como decisiones de adecuación o cláusulas contractuales tipo aprobadas por la Comisión Europea.",
        ],
      },
      {
        h2: "Derechos de las personas interesadas",
        paragraphs: [
          "Puedes ejercer los derechos de acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad, así como retirar el consentimiento cuando este sea la base jurídica, escribiendo a " + business.email + " e indicando el derecho que deseas ejercer; si existieran dudas razonables sobre tu identidad, podría solicitarte un documento acreditativo.",
          "También tienes derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es).",
        ],
      },
      {
        h2: "Medidas de seguridad",
        paragraphs: [
          "Se aplican medidas técnicas y organizativas apropiadas al riesgo: comunicaciones cifradas mediante TLS, control de acceso al área de gestión, limitación de los datos recogidos a los estrictamente necesarios y validación de los documentos adjuntos. No obstante, ningún sistema es absolutamente invulnerable; en caso de incidente de seguridad que afecte a tus datos se actuará conforme a las obligaciones legales de notificación.",
        ],
      },
    ],
  },
  "politica-de-cookies": {
    title: "Política de cookies",
    seoTitle: "Política de cookies | MideFinca",
    metaDescription: "Política de cookies de MideFinca: auditoría real de cookies y almacenamiento local, consentimiento y cómo configurar tu elección.",
    updated: "15 de julio de 2026",
    sections: [
      {
        h2: "Qué son las cookies",
        paragraphs: [
          "Las cookies son pequeños archivos que los sitios web instalan en el navegador. Existen tecnologías similares, como el almacenamiento local (localStorage), que cumplen funciones parecidas. Algunas son imprescindibles para que la web funcione (técnicas o necesarias) y están exentas de consentimiento; otras, como las de analítica o publicidad, solo pueden instalarse si el usuario las acepta previamente.",
        ],
      },
      {
        h2: "Qué utiliza realmente esta web",
        paragraphs: [
          "Tras auditar técnicamente el sitio: midefinca.es no instala actualmente ninguna cookie propia ni de terceros. No hay analítica, publicidad, píxeles de seguimiento ni mapas o vídeos incrustados de terceros. Las fuentes tipográficas están autoalojadas, por lo que la visita no envía datos a CDNs de fuentes externos.",
        ],
      },
      {
        h2: "Cookies de analítica",
        paragraphs: [
          "Actualmente esta web no utiliza cookies de analítica ni de medición: tu visita no se registra con fines estadísticos.",
          "Si en el futuro se activara una herramienta de estadística de visitas (por ejemplo, Google Analytics), solo se instalaría si la aceptas previamente desde el aviso de cookies. En ese caso, esta página se actualizaría para informarte de qué cookies se usarían, con qué finalidad y durante cuánto tiempo.",
          "Nunca se instalarán cookies de publicidad ni de seguimiento sin informarte antes en esta página y sin tu consentimiento previo.",
        ],
      },
      {
        h2: "Sistema de consentimiento",
        paragraphs: [
          "Cuando existan cookies no exentas, la web mostrará un aviso en primera capa con las opciones Aceptar, Rechazar y Configurar, con visibilidad equivalente. Antes de tu decisión no se carga ninguna tecnología no esencial; rechazar impide su instalación, y configurar permite decidir por categorías. Tu elección queda guardada en tu navegador y, mientras haya cookies no exentas activas, dispondrás del enlace «Configurar cookies» en el pie de página para modificarla o retirarla con la misma facilidad con la que la otorgaste.",
          "No se considerará consentimiento el seguir navegando, hacer scroll, cerrar el aviso o permanecer en la página.",
        ],
      },
      {
        h2: "Cómo eliminar cookies y almacenamiento local",
        paragraphs: [
          "Puedes eliminar o bloquear cookies y el almacenamiento local desde la configuración de tu navegador: Chrome (Configuración → Privacidad y seguridad → Cookies), Firefox (Ajustes → Privacidad y seguridad), Safari (Preferencias → Privacidad) y Edge (Configuración → Cookies y permisos del sitio). Borrar los datos de este sitio elimina también la preferencia guardada, y el aviso volverá a mostrarse si existen cookies no exentas activas.",
        ],
      },
      {
        h2: "Tratamiento por terceros y transferencias internacionales",
        paragraphs: [
          "Actualmente ningún tercero recibe datos mediante cookies o tecnologías similares. Si se activara la analítica de Google, dicho proveedor podría realizar transferencias internacionales de datos amparadas en los mecanismos previstos por el RGPD; esta página se actualizaría para reflejarlo.",
        ],
      },
    ],
  },
};

export default function Legal({ page }) {
  const lang = useLang();
  const cr = useCopy("crumbs");
  const lg = useCopy("legal");
  const lp = useLp();
  const base = pages[page];
  const en = lang === "en" ? legalEn[page] : null;
  const content = en ? { ...base, ...en } : base;
  const crumbs = [
    { name: cr.home, path: lp("/") },
    { name: content.title, path: lp(`/${page}/`) },
  ];
  return (
    <>
      <SEO title={content.seoTitle} description={content.metaDescription} path={`/${page}/`} jsonLd={[breadcrumbSchema(crumbs)]} />
      <Breadcrumbs items={crumbs} />
      <section className="container-site section-gap !pt-10" data-testid={`legal-page-${page}`}>
        <p className="tech-label mb-4">Legal</p>
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight">{content.title}</h1>
        <div className="max-w-3xl">
          {lang === "en" && (
            <p className="mt-5 text-[13px] text-slate-600 bg-paper-tint border hairline rounded-sm px-4 py-3" data-testid="legal-en-note">
              {lg.note}
            </p>
          )}
          <p className="mt-4 font-mono text-xs text-slate-600 tracking-wider">{lg.updated} {content.updated}</p>
          {content.sections.map((s, i) => (
            <section key={i} className="mt-10">
              <h2 className="text-xl sm:text-2xl font-semibold text-ink mb-4">{s.h2}</h2>
              {s.paragraphs.map((p, j) => (
                <p key={j} className="text-[15px] text-slate-700 leading-relaxed mb-3">{p}</p>
              ))}
              {s.list && (
                <ul className="space-y-2.5 mt-2">
                  {s.list.map((item, k) => (
                    <li key={k} className="flex gap-3 text-[15px] text-slate-700 leading-relaxed">
                      <span className="font-mono text-xs text-terracotta mt-1 shrink-0">·</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
              {s.after && s.after.map((p, j) => (
                <p key={`after-${j}`} className="text-[15px] text-slate-700 leading-relaxed mb-3 mt-4">
                  {p.text}
                  {p.href && (
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="legal-external-link"
                      className="text-[#C56D42] underline underline-offset-2 hover:text-[#B45D3A] transition-colors break-all"
                    >
                      {p.linkText}
                    </a>
                  )}
                </p>
              ))}
            </section>
          ))}
        </div>
      </section>
    </>
  );
}
